import { useCallback, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { SectionHeader } from '@/components/molecules/SectionHeader';
import type { Character, MoveRef } from '@/schemas/character';
import type { ClassDefinition } from '@/schemas/classDefinition';
import { hasMoveSetup, type Move } from '@/schemas/move';
import { findMoveByIdGlobal } from '@/data/moves';
import { getEffectiveBaseHp } from '@/services/characterCreation';
import { renderInlineMarkdown } from '@/utils/inlineMarkdown';
import { MoveSetupPanel } from '../../sections/MoveSetupPanel';
import { updateMoveChoiceGroup, updateMoveNarrativeField } from '../../services/moveRefMutations';

// page-specific (Fase 2 PR 9): step «2° Archetipo» del flusso di salita di
// livello. Pixel-parity dal pattern mockup `.lvup-stat-card` (styles.css
// 1921-1985), adattato semanticamente per le card archetipo (no row stat
// cur→next, ma name + description + eventuale delta HP).
//
// Visibile SOLO se la mossa scelta è *Infanzia Complessa* (manuale V1
// r. 1787). Lista degli archetipi della classe del PG, escluso quello
// primario.
//
// **FI-1 esteso**: se il 2° archetipo definisce `baseHpOverride`, mostra
// il delta HP atteso (D-Q14 SSOT applicata anche al delta da
// `baseHpOverride`). Caso noto: Paladino-Umano + Nano → +2 HP.
//
// **PR 9 cambio API**: i bottoni Indietro/Avanti sono delegati a
// `<StepNav>` esterno renderizzato da `LevelUpFlowPage`. Le prop `onNext`/
// `onBack` sono rimosse.

interface LevelUpStepArchetypeProps {
  character: Character;
  classDef: ClassDefinition;
  selected: string | undefined;
  onSelect: (archetypeId: string) => void;
  /**
   * **FI-80 (2026-05-24)** — Setup compilato della mossa-archetipo del 2°
   * archetipo se la mossa ha `hasMoveSetup` (es. `mago.archetipo-umano`
   * con 18 spell choices). `undefined` se nessun archetipo selezionato o
   * mossa-archetipo description-only.
   */
  archetypeSetup: Omit<MoveRef, 'id'> | undefined;
  onArchetypeSetupChange: (setup: Omit<MoveRef, 'id'> | undefined) => void;
}

export function LevelUpStepArchetype({
  character,
  classDef,
  selected,
  onSelect,
  archetypeSetup,
  onArchetypeSetupChange,
}: LevelUpStepArchetypeProps) {
  // Filtra gli archetipi della classe escludendo quello primario.
  const candidates = classDef.archetypes.filter((a) => a.id !== character.archetypeId);
  const currentBaseHp = getEffectiveBaseHp(classDef, character.archetypeId, undefined);
  const primaryArchetypeName =
    classDef.archetypes.find((a) => a.id === character.archetypeId)?.name ?? character.archetypeId;

  // **FI-80**: risolve la mossa-archetipo del 2° archetipo selezionato per
  // esporre il `MoveSetupPanel` se ha setup interattivo.
  const selectedArchetype = candidates.find((a) => a.id === selected);
  const archetypeMove: Move | undefined = selectedArchetype
    ? findMoveByIdGlobal(selectedArchetype.grantedMoveId)
    : undefined;
  const archetypeMoveNeedsSetup = archetypeMove !== undefined && hasMoveSetup(archetypeMove);

  return (
    <section>
      <SectionHeader
        title="Secondo archetipo (Infanzia Complessa)"
        sub={`Manuale V1 r. 1787: scegli un secondo archetipo tra quelli della classe ${classDef.name}. Il tuo archetipo primario è ${primaryArchetypeName}.`}
      />
      <div className="mt-4 grid grid-cols-1 gap-[0.6rem] sm:grid-cols-2">
        {candidates.map((arch) => {
          const projectedBaseHp = getEffectiveBaseHp(classDef, character.archetypeId, arch.id);
          const hpDelta = projectedBaseHp - currentBaseHp;
          return (
            <ArchetypeCard
              key={arch.id}
              name={arch.name}
              description={arch.description}
              hpDelta={hpDelta}
              selected={selected === arch.id}
              onSelect={() => onSelect(arch.id)}
            />
          );
        })}
      </div>
      {archetypeMoveNeedsSetup && archetypeMove !== undefined && (
        <ArchetypeMoveSetupSection
          character={character}
          move={archetypeMove}
          setup={archetypeSetup}
          onChange={onArchetypeSetupChange}
        />
      )}
    </section>
  );
}

interface ArchetypeMoveSetupSectionProps {
  character: Character;
  move: Move;
  setup: Omit<MoveRef, 'id'> | undefined;
  onChange: (next: Omit<MoveRef, 'id'> | undefined) => void;
}

/**
 * **FI-80 (2026-05-24)** — Pannello di setup per la mossa-archetipo del
 * 2° archetipo IC. Pattern coerente con `BundleSetupsSection` /
 * `NestedMoveSetup` (estensione naturale dello step responsabile).
 */
function ArchetypeMoveSetupSection({
  character,
  move,
  setup,
  onChange,
}: ArchetypeMoveSetupSectionProps) {
  const ownedMoveIds = useMemo(() => character.moves.map((m) => m.id), [character.moves]);
  const moveRef = useMemo<MoveRef>(
    () => ({
      id: move.id,
      selectedChoices: setup?.selectedChoices ?? [],
      ...(setup?.selectedChoiceGroups && { selectedChoiceGroups: setup.selectedChoiceGroups }),
      ...(setup?.narrativeFieldValues && { narrativeFieldValues: setup.narrativeFieldValues }),
    }),
    [move.id, setup],
  );
  const handleChoiceGroupChange = useCallback(
    (_moveId: string, groupId: string, selectedIds: string[]): void => {
      const updated = updateMoveChoiceGroup(moveRef, moveRef.id, groupId, selectedIds);
      onChange(extractSetup(updated));
    },
    [moveRef, onChange],
  );
  const handleNarrativeFieldChange = useCallback(
    (_moveId: string, fieldId: string, value: string): void => {
      const updated = updateMoveNarrativeField(moveRef, moveRef.id, fieldId, value);
      onChange(extractSetup(updated));
    },
    [moveRef, onChange],
  );
  return (
    <div className="border-rule bg-paper-soft/40 mt-4 border p-3">
      <p className="font-display text-ink-soft mb-2 text-[0.78rem] tracking-[0.08em] uppercase">
        Setup di «{move.name}»
      </p>
      <MoveSetupPanel
        move={move}
        selectedByGroup={buildSelectedByGroup(move, moveRef)}
        narrativeValues={moveRef.narrativeFieldValues ?? {}}
        recruitOutcome={moveRef.recruitOutcome}
        archetypeId={character.archetypeId}
        ownedMoveIds={ownedMoveIds}
        onChoiceGroupChange={handleChoiceGroupChange}
        onNarrativeFieldChange={handleNarrativeFieldChange}
      />
    </div>
  );
}

function buildSelectedByGroup(move: Move, moveRef: MoveRef): Record<string, string[]> {
  const result: Record<string, string[]> = {};
  if (move.choices) result.main = [...moveRef.selectedChoices];
  if (moveRef.selectedChoiceGroups) {
    for (const [groupId, ids] of Object.entries(moveRef.selectedChoiceGroups)) {
      result[groupId] = [...ids];
    }
  }
  return result;
}

function extractSetup(moveRef: MoveRef): Omit<MoveRef, 'id'> {
  const setup: Omit<MoveRef, 'id'> = { selectedChoices: moveRef.selectedChoices };
  if (moveRef.selectedChoiceGroups) setup.selectedChoiceGroups = moveRef.selectedChoiceGroups;
  if (moveRef.narrativeFieldValues) setup.narrativeFieldValues = moveRef.narrativeFieldValues;
  return setup;
}

interface ArchetypeCardProps {
  name: string;
  description: string | undefined;
  hpDelta: number;
  selected: boolean;
  onSelect: () => void;
}

function ArchetypeCard({ name, description, hpDelta, selected, onSelect }: ArchetypeCardProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        'border-rule bg-paper-bright relative cursor-pointer border-[1.5px] px-4 py-[0.9rem] text-left transition-[border-color,background-color,transform] duration-150',
        'hover:border-ink hover:-translate-y-px',
        selected && 'border-ink bg-paper-soft',
      )}
    >
      {selected ? (
        <span
          className="bg-ink pointer-events-none absolute top-0 bottom-0 left-0 w-[4px]"
          aria-hidden
        />
      ) : null}
      <div className="font-display text-ink mb-[0.4rem] text-[0.85rem] tracking-[0.08em] uppercase">
        {name}
        {selected ? <span className="text-ink font-bold"> ✓</span> : null}
      </div>
      {description ? (
        <p className="text-ink-soft font-serif text-[0.88rem] leading-snug italic">
          {renderInlineMarkdown(description)}
        </p>
      ) : null}
      {hpDelta !== 0 ? (
        <p className="font-display text-accent-dwr mt-[0.5rem] text-[0.7rem] tracking-[0.08em] uppercase">
          PF base {hpDelta > 0 ? `+${hpDelta}` : hpDelta}
        </p>
      ) : null}
    </button>
  );
}
